import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Drawer, IconButton, Switch, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch, useSelector } from 'react-redux';
import type { ICampaign, IDeliverable, IDeliverableRound } from '../../../redux/buzzbridge/types';
import { updateOffsiteCampaign } from '../../../redux/buzzbridge/actions';
import { updatingCampaignSelector } from '../../../redux/buzzbridge/selectors';
import { DeliverableInlineEditor } from '../../../components/Campaign/DeliverableInlineEditor';
import { DeliverableRoundBuilder } from '../../../components/Campaign/DeliverableRoundBuilder';
import {
    BRAND_LUXE_PRIMARY,
    BRAND_SURFACE_CONTAINER_LOW,
    BRAND_SURFACE_CONTAINER,
    BRAND_OUTLINE_VARIANT,
} from '../brandDashboardTokens';
import { FONT_ENUM } from '../../../types/global/fonts.types';

function FieldLabel({ children }: { children: React.ReactNode }) {
    return (
        <Typography
            sx={{
                fontFamily: FONT_ENUM.MAIN_BOLD,
                fontSize: 11,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: '#fff',
                mb: 1.5,
            }}>
            {children}
        </Typography>
    );
}

interface Props {
    open: boolean;
    onClose: () => void;
    campaign: ICampaign;
}

export function CampaignDeliverablesPanel({ open, onClose, campaign }: Props) {
    const dispatch = useDispatch();
    const updating = useSelector(updatingCampaignSelector);

    const [recurrentDeliverables, setRecurrentDeliverables] = useState(false);
    const [deliverables, setDeliverables] = useState<IDeliverable[]>([]);
    const [deliverableRounds, setDeliverableRounds] = useState<IDeliverableRound[]>([]);

    useEffect(() => {
        if (!open) return;
        setRecurrentDeliverables(Boolean((campaign as any).recurrent_deliverables));
        setDeliverables([...(campaign.deliverables || [])]);
        setDeliverableRounds([...((campaign as any).deliverable_rounds || [])]);
    }, [open, campaign]);

    const isDirectHire = campaign.campaign_type === 'direct_hire';
    const platforms = (campaign.platforms || []) as string[];
    const syncUpload = campaign.campaign_type === 'sync_upload';
    const recurringLockedBySync = syncUpload && !recurrentDeliverables;

    const handleSave = () => {
        const body: Record<string, any> = {};
        const origRecurrent = Boolean((campaign as any).recurrent_deliverables);

        if (recurrentDeliverables !== origRecurrent) body.recurrent_deliverables = recurrentDeliverables;

        if (recurrentDeliverables) {
            body.deliverable_rounds = deliverableRounds.map((r: any, i: number) => ({
                ...r,
                round_number: i + 1,
            }));
            if ((campaign as any).delivery_date) body.delivery_date = null;
        } else {
            const origDeliverables = JSON.stringify(campaign.deliverables || []);
            const deliverablesForSubmit = isDirectHire
                ? deliverables.map(({ platform: _p, ...rest }) => rest as IDeliverable)
                : deliverables;
            if (JSON.stringify(deliverablesForSubmit) !== origDeliverables) {
                body.deliverables = deliverablesForSubmit;
            }
        }

        if (Object.keys(body).length === 0) {
            onClose();
            return;
        }
        dispatch(updateOffsiteCampaign({ campaignId: campaign._id, body }));
        onClose();
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: { xs: '100vw', sm: 580 },
                    bgcolor: '#111',
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}>
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 3,
                    py: 2.5,
                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                }}>
                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 16, color: '#fff' }}>
                    Deliverables
                </Typography>
                <IconButton size="small" onClick={onClose} sx={{ color: 'rgba(255,255,255,0.5)' }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            {/* Body */}
            <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Recurring toggle */}
                {!campaign.commission && !(campaign as any).app_install?.enabled && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 2,
                            borderRadius: 2,
                            bgcolor: recurrentDeliverables ? 'rgba(255,201,101,0.08)' : BRAND_SURFACE_CONTAINER_LOW,
                            border: '1px solid',
                            borderColor: recurrentDeliverables ? 'rgba(255,201,101,0.25)' : 'rgba(255,255,255,0.06)',
                        }}>
                        <Box>
                            <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 13, color: '#fff' }}>
                                Recurring deliverables
                            </Typography>
                            <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', mt: 0.25 }}>
                                {recurringLockedBySync
                                    ? 'Sync upload is on, so a recurring series stays off. Turn off sync upload first — they cannot both be on.'
                                    : recurrentDeliverables
                                    ? 'Multiple rounds, each with its own deadline'
                                    : 'Creators submit once against a single delivery date'}
                            </Typography>
                            {syncUpload && recurrentDeliverables ? (
                                <Typography sx={{ fontSize: 12, color: BRAND_LUXE_PRIMARY, mt: 0.5 }}>
                                    Sync upload and a recurring series cannot both be on. Turn one off before you save.
                                </Typography>
                            ) : null}
                        </Box>
                        <Switch
                            size="small"
                            checked={recurrentDeliverables}
                            disabled={campaign.status === 'published' || recurringLockedBySync}
                            onChange={(_, v) => {
                                setRecurrentDeliverables(v);
                            }}
                            sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                    color: BRAND_LUXE_PRIMARY,
                                    '& + .MuiSwitch-track': {
                                        backgroundColor: 'rgba(255,201,101,0.4)',
                                        opacity: 1,
                                    },
                                },
                                '& .MuiSwitch-track': { opacity: 1, backgroundColor: BRAND_OUTLINE_VARIANT },
                            }}
                        />
                    </Box>
                )}

                {/* Editor */}
                <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: 3, borderRadius: 2 }}>
                    <FieldLabel>{recurrentDeliverables ? 'Delivery schedule' : 'Deliverables'}</FieldLabel>
                    {recurrentDeliverables ? (
                        <DeliverableRoundBuilder
                            rounds={deliverableRounds}
                            onRoundsChange={setDeliverableRounds}
                            selectedPlatforms={platforms}
                            campaignType={campaign.campaign_type}
                            startDate={(campaign as any).start_date}
                            endDate={(campaign as any).end_date}
                        />
                    ) : (
                        <DeliverableInlineEditor
                            items={deliverables}
                            onItemsChange={setDeliverables}
                            selectedPlatforms={platforms}
                            isDirectHire={isDirectHire}
                        />
                    )}
                </Box>
            </Box>

            {/* Footer */}
            <Box
                sx={{
                    px: 3,
                    py: 2.5,
                    borderTop: '1px solid rgba(255,255,255,0.06)',
                    bgcolor: `${BRAND_SURFACE_CONTAINER}80`,
                    display: 'flex',
                    gap: 2,
                    flexDirection: 'row-reverse',
                }}>
                <Button
                    onClick={handleSave}
                    disabled={updating}
                    sx={{
                        px: 4,
                        py: 1.5,
                        bgcolor: BRAND_LUXE_PRIMARY,
                        color: '#000',
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        borderRadius: 3,
                        '&:hover': { bgcolor: '#e6b500' },
                        '&.Mui-disabled': { bgcolor: 'rgba(255,201,101,0.3)', color: 'rgba(0,0,0,0.4)' },
                        minWidth: 100,
                    }}>
                    {updating ? <CircularProgress size={16} sx={{ color: '#000' }} /> : 'Save'}
                </Button>
                <Button
                    onClick={onClose}
                    sx={{
                        px: 3,
                        py: 1.5,
                        color: 'rgba(255,255,255,0.6)',
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        borderRadius: 3,
                        border: '1px solid rgba(255,255,255,0.1)',
                    }}>
                    Cancel
                </Button>
            </Box>
        </Drawer>
    );
}
